import { useTranslation } from "react-i18next";

import { deductMainSpeakers } from "@/helpers/participants/participants.helpers";

import { SimpleHeaderProps, TitleProps } from "./Title.decl";
import * as S from "./Title.styled";
import { bindComponentsToSpeakers, joinElements } from "./Title.utilities";

import { SimpleSpeakerAvatar } from "@/components/SpeakerAvatar/SpeakerAvatar";
import { hasVirtualAgentCallTag } from "@/helpers/tag/hasVitualAgentCallTag";
import { useBaseStore } from "@/store/store";
import { Flex } from "@aircall/tractor";

function Inbound({ from, to }: SimpleHeaderProps) {
  const { t } = useTranslation();

  if (!from || !to) {
    return null;
  }

  return (
    <S.Wrapper>
      <Flex gap="xxxs">
        <SimpleSpeakerAvatar variant="green" name={to.fullName} />
        <S.Agent>{to.displayName}</S.Agent>
      </Flex>

      <S.Text>{t("call_details.took_call")}</S.Text>

      <Flex gap="xxxs">
        <SimpleSpeakerAvatar variant="blue" name={from.fullName} />
        <S.Client>{from.displayName}</S.Client>
        {from.companyName && (
          <S.Text>
            {t("call_details.at_company", { company: from.companyName })}
          </S.Text>
        )}
      </Flex>
    </S.Wrapper>
  );
}

function Outbound({ to, from }: SimpleHeaderProps) {
  const { t } = useTranslation();

  if (!from || !to) {
    return null;
  }

  return (
    <S.Wrapper>
      <Flex gap="xxxs">
        <SimpleSpeakerAvatar variant="green" name={from.fullName} />
        <S.Agent>{from.displayName}</S.Agent>
      </Flex>

      <S.Text>{t("call_details.called")}</S.Text>

      <Flex gap="xxxs">
        <SimpleSpeakerAvatar variant="blue" name={to.fullName} />
        <S.Client>{to.displayName}</S.Client>
        {to.companyName && (
          <S.Text>
            {t("call_details.at_company", { company: to.companyName })}
          </S.Text>
        )}
      </Flex>
    </S.Wrapper>
  );
}

export function Voicemail({ from }: Pick<SimpleHeaderProps, "from">) {
  const { t } = useTranslation();
  const activeTags = useBaseStore((state) => state.activeTags);
  const isVirtualCall = hasVirtualAgentCallTag(activeTags);

  if (!from) {
    return null;
  }

  return (
    <S.Wrapper>
      <Flex gap="xxxs">
        {from.fullName && (
          <SimpleSpeakerAvatar variant="yellow" name={from.fullName} />
        )}
        <S.SpeakerName variant="headingBoldS" ellipsis>
          {from.displayName}
        </S.SpeakerName>
      </Flex>

      <Flex gap="xxxs">
        {from.companyName && (
          <S.Text>
            {t("call_details.at_company", { company: from.companyName })}
          </S.Text>
        )}
      </Flex>

      <Flex gap="xxxs">
        <S.Text>
          {t(
            isVirtualCall
              ? "call_details.virtual_agent_call"
              : "call_details.left_voicemail"
          )}
        </S.Text>
      </Flex>
    </S.Wrapper>
  );
}

const TITLE_MAPPING = {
  inbound: Inbound,
  outbound: Outbound,
  voicemail: Voicemail,
};

/**
 * If there are less than 2 speakers, we render Title using from and to props
 * Otherwise, we render the Title with the speakers props
 */
export function Title({ type, from, to, speakers }: TitleProps) {
  const { t } = useTranslation();

  if (!from || !to) {
    return null;
  }

  // if speakers is undefined or we have only 2 speakers, we render the title with `from` and `to`
  if (!speakers || speakers.size === 2) {
    const TitleComponent = TITLE_MAPPING[type];
    return <TitleComponent from={from} to={to} />;
  }

  const isOutbound = type === "outbound";

  const { mainAgentId, mainExternalNumber } = deductMainSpeakers({
    type,
    from,
    to,
  });

  const { MainAgent, OtherAgents, ExternalUser, OtherUsers } =
    bindComponentsToSpeakers(speakers, mainAgentId, mainExternalNumber);

  if (isOutbound) {
    // return "MainAgent and OtherAgent call ExternalUser and OtherUsers"
    return (
      <S.Wrapper>
        {joinElements(
          MainAgent,
          OtherAgents,
          <S.Text>{t("call_details.and")}</S.Text>
        )}
        <S.Text>{t("call_details.called")}</S.Text>
        {joinElements(
          ExternalUser,
          OtherUsers,
          <S.Text>{t("call_details.and")}</S.Text>
        )}
      </S.Wrapper>
    );
  }

  return (
    // return "MainAgent and OtherAgent took a call from ExternalUser and OtherUsers"
    <S.Wrapper>
      {joinElements(
        MainAgent,
        OtherAgents,
        <S.Text>{t("call_details.and")}</S.Text>
      )}
      <S.Text>{t("call_details.took_call")}</S.Text>
      {joinElements(
        ExternalUser,
        OtherUsers,
        <S.Text>{t("call_details.and")}</S.Text>
      )}
    </S.Wrapper>
  );
}
